/*
 * Palco - CHASSIS state (genérico).
 *
 * Este ficheiro define APENAS o que qualquer produto que usar o palco precisa:
 * navegação (stage, offerSubStep, loading, visitedStages) e a vocabulário de
 * stage identifiers. Product-specific data vive em `palco.<product>.ts`
 * (ex.: `palco.colibike.ts` para Colibike; futuro `palco.colisport.ts` para
 * Colisport) e é agregada em `PalcoState` como sub-slices por produto.
 *
 * Regra: adicionar aqui é uma decisão sobre a arquitetura do palco (afeta
 * todos os produtos). Adicionar em `palco.<product>.ts` é uma decisão sobre
 * UM produto. Se estás em dúvida em qual dos dois pôr uma field, o produto
 * ganha - o chassis fica magro.
 *
 * Persistência scoped: apenas `state.palco` é escrito para sessionStorage - o
 * resto do StepsFormState fica in-memory como sempre foi. Ver `persistPalco.ts`.
 */

import type { ColibikeSlice } from './palco.colibike';
import { initialColibikeSlice } from './palco.colibike';

/* ── 12 estados discretos do palco ─────────────────────────────────────── *
 * Vocabulário partilhado entre produtos - cada produto define no seu ficheiro
 * a SUBSET (via sequence) que usa. Um novo stage exclusivo a um produto pode
 * ser adicionado a este union (aditivo) - mas se acontecer com frequência,
 * considerar tornar StageId genérico (`string`) e mover o union para cada
 * produto.
 *
 * DELIBERADAMENTE não existe stage='date': o sub-step calendar/slot vive
 * DENTRO do stage='offer' via `offerSubStep`. Duas fontes para o mesmo bit
 * permitiam estados impossíveis (ex.: stage='date' + offerSubStep='offer'). */
export type StageId =
  | 'photo' //           estado 0 - front do flipper existente (bike.mp4 / photo)
  | 'type' //            A1 - Type de vélo
  | 'quantite' //        A2 - Quantité & poids
  | 'origine' //         A3 - Origine (shop pickup)
  | 'destination' //     A4 - Destination (relais OU adresse)
  | 'loader-offers' //   transição A -> B (cyclist animado)
  | 'offer' //           B1  - Choisir l'offre (slider + sub-step date via offerSubStep)
  | 'contact' //         B2 - Expéditeur + Destinataire
  | 'recap' //           B3 - Récapitulatif
  | 'paiement' //        B4 - Paiement (billing + card)
  | 'loader-pay' //      transição paiement -> done
  | 'done'; //           Confirmation

/* ── Chassis state - navegação e loader ────────────────────────────────── */
export interface PalcoChassisState {
  /** Stage atualmente visível. Persistido; safety: stages terminais
   *  ('done', 'loader-*') NÃO são re-hidratáveis (ver persistPalco). */
  stage: StageId;

  /** B1 tem dois sub-steps na mesma pill: slider de ofertas + calendar/slots.
   *  Modelado como sub-step para não inflacionar StageId. */
  offerSubStep: 'offer' | 'date';

  /** Loader LOCAL ao palco (cyclist SVG), NÃO o useLoader global.
   *  `null` = sem loader visível. Não persistido (refresh mid-loader não deve
   *  deixar o palco preso em "a procurar ofertas..."). */
  loading: null | 'offers' | 'payment';

  /** Array (não Set) para persistência JSON-safe. Alimenta o pillState. */
  visitedStages: StageId[];
}

export const initialChassisState: PalcoChassisState = {
  // Fase 2: A1 ('type') foi fundido em 'quantite'. Sequência do Colibike
  // arranca em 'quantite' (após o flipper photo). Nota: o chassis conhece
  // este nome de stage (mínimo acoplamento a produto). Cleanup possível:
  // mover initial.stage para o produto (initialColibikeSlice teria um
  // `stage` override). Adiado - não bloqueia.
  stage: 'quantite',
  offerSubStep: 'offer',
  loading: null,
  visitedStages: [],
};

/* ── State completo do palco = chassis + slot por produto ──────────────── *
 * Cada produto tem o SEU sub-slice. Nunca são lidos ou escritos por
 * componentes de outro produto. Quando Colisport for adicionado:
 *   export interface PalcoState extends PalcoChassisState {
 *     colibike: ColibikeSlice;
 *     colisport: ColisportSlice;
 *   }
 * Só um dos dois é usado por sessão (utilizador está num produto de cada vez). */
export interface PalcoState extends PalcoChassisState {
  colibike: ColibikeSlice;
}

export const initialPalcoState: PalcoState = {
  ...initialChassisState,
  colibike: initialColibikeSlice,
};
